import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../Common/Loading';
import CustomConfirmation from '../Common/CustomConfirmation';
import CustomAlert from '../Common/CustomAlert';
import popimg from "../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg from "../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../Common/Images/ImagesForAlert/popup_close_n.png";
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const COMPETENCY_LEVELS = [
    { value: 0, label: '-- Select --' },
    { value: 1, label: 'Back 2.5 grades' },
    { value: 2, label: 'Back 1.5 grades' },
    { value: 3, label: 'Back 0.5 grades' },
    { value: 4, label: 'Beginning current grade' },
    { value: 5, label: 'Mid current grade' },
];

const YEAR_OPTIONS = [
    { value: '-1', label: '-- Select --' },
    ...Array.from({ length: 13 }, (_, i) => ({ value: String(i), label: String(i) }))
];

const EL_CreateDefaultLessonPlan: React.FC = () => {
    const [searchParams] = useSearchParams();

    const schoolCode = searchParams.get('C') || '';
    const familyCode = searchParams.get('F') || '';
    const login = searchParams.get('L') || '';
    const subject = searchParams.get('Subject') || 'Mathematics';

    const studyYearParam = searchParams.get('GrdYr') || '0';
    const countryParam = searchParams.get('KT') || 'Australia';

    const [studentName, setStudentName] = useState<string>('');
    const [currentCompetency, setCurrentCompetency] = useState<number>(0);
    const [competency, setCompetency] = useState<number>(0);
    const [existingWLP, setExistingWLP] = useState<string>('');

    const [requestedStudyYear, setRequestedStudyYear] = useState<string>(studyYearParam);
    const [lessonPlanYear, setLessonPlanYear] = useState<string>('0');
    const [targetCompetency, setTargetCompetency] = useState<number>(0);
    const [isSelective, setIsSelective] = useState<boolean>(false);

    const [loading, setLoading] = useState<boolean>(true);

    const navigate = useNavigate()

    const [confirmModal, setConfirmModal] = useState<{
        show: boolean;
        message: string;
        onConfirm: () => void;
    }>({ show: false, message: '', onConfirm: () => { } });

    const [alertModal, setAlertModal] = useState<{
        show: boolean;
        message: string;
        onCloseCallback?: () => void;
    }>({ show: false, message: '' });

    const getCurriculum = (cntry: string) => {
        if (cntry.toUpperCase() === 'AU' || cntry === 'Australia') return 'AUSVELS';
        if (cntry.toUpperCase() === 'UK') return 'National Curriculum';
        return 'Standard';
    };

    const getCompetencyLabel = (val: number | string) => {
        const numericVal = typeof val === 'string' ? parseInt(val, 10) : val;
        const found = COMPETENCY_LEVELS.find(c => c.value === numericVal);
        return found ? found.label : '--';
    };

    useEffect(() => {
        document.body.style.backgroundColor="#FFF"
        fetchStudentStatus();
    }, []);

    const fetchStudentStatus = async () => {
        try {
            const url = new URL(`${API_BASE_URL}api/wlp/get_student_status/`);
            url.searchParams.append('SchoolCode', schoolCode);
            url.searchParams.append('FamilyCode', familyCode);
            url.searchParams.append('Login', login);
            url.searchParams.append('Subject', subject);

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();

            if (data.status === 'success') {
                setStudentName(data.studentName);
                setExistingWLP(data.existingWLPType || '');
                setIsSelective(data.isSelective);

                const rawCompVal = Number(data.currentCompetency) || 0;
                const mappedCompetency = rawCompVal > 0 ? rawCompVal : 2;
                setCurrentCompetency(mappedCompetency);
                setTargetCompetency(mappedCompetency);
                const baseCompetency = Number(data.competency) || 0;
                setCompetency(baseCompetency);

                const resolvedYear = data.studyYear && Number(data.studyYear) > 0
                    ? String(data.studyYear)
                    : (studyYearParam && studyYearParam !== '0' ? studyYearParam : '0');
                setLessonPlanYear(resolvedYear);
            }
        } catch (error) {
            console.error("Error fetching student info", error);
        } finally {
            setLoading(false);
        }
    };

    const handleStartCreate = () => {
        if (lessonPlanYear === '-1' || lessonPlanYear === '' || lessonPlanYear === '0') {
            setAlertModal({ show: true, message: "Please select student Lesson plan year." });
            return;
        }

        if (targetCompetency <= 0) {
            setAlertModal({ show: true, message: "Please select Lesson Plan Competency." });
            return;
        }

        if (existingWLP && existingWLP !== '') {
            setConfirmModal({
                show: true,
                message: `${studentName} already has ${existingWLP} Lesson plan.<br/><br/>Do you really want to overwrite?`,
                onConfirm: () => {
                    setConfirmModal(prev => ({ ...prev, show: false }));
                    checkSelectiveStatus();
                }
            });
        } else {
            checkSelectiveStatus();
        }
    };

    const checkSelectiveStatus = () => {
        if (isSelective) {
            setConfirmModal({
                show: true,
                message: "Are you sure this student is a Selective Student?",
                onConfirm: () => {
                    setConfirmModal(prev => ({ ...prev, show: false }));
                    submitPlan();
                }
            });
        } else {
            submitPlan();
        }
    };

    const submitPlan = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}api/wlp/create_plan/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    SchoolCode: schoolCode,
                    FamilyCode: familyCode,
                    Login: login,
                    Subject: subject,
                    Competency: targetCompetency,
                    StudyYear: lessonPlanYear,
                    IsSelective: isSelective,
                    KT: countryParam,
                    CreatedBy: sessionStorage.getItem('L')
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setAlertModal({
                    show: true,
                    message: 'Lesson Plan Created Successfully.',
                    onCloseCallback: () => {
                        if (window.opener) window.close();
                        else window.history.back();
                    }
                });
            } else {
                setAlertModal({ show: true, message: 'Failed: ' + (data.message || 'Unknown error') });
            }
        } catch (error) {
            console.error(error);
            setAlertModal({ show: true, message: 'An error occurred.' });
        } finally {
            setLoading(false);
        }
    };

    const containerStyle: React.CSSProperties = {
        fontFamily: 'Arial, sans-serif',
        fontSize: '14pt',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // backgroundColor: '#fff',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        padding: '20px',
        boxSizing: 'border-box',
        overflowY: 'auto'
    };

    const headerStyle: React.CSSProperties = {
        backgroundColor: '#cccccc',
        color: 'black',
        // fontWeight: 'bold',
        padding: '8px 15px',
        width: '100%',
        maxWidth: '100%',
        textAlign: 'center',
        marginBottom: '15px',
        border: '1px solid #003366',
        boxSizing: 'border-box',
        fontSize: "14pt",
        borderTop:"none",
        borderBottom:"none"
    };

    const rowStyle: React.CSSProperties = {
        display: 'flex',
        width: '100%',
        maxWidth: '100%',
        marginBottom: '6px',
        alignItems: 'center'
    };

    const labelStyle: React.CSSProperties = {
        width: '45%',
        textAlign: 'right',
        paddingRight: '15px',
        paddingBottom:"5px",
        // fontWeight: 'bold',
        color: '#000000'
    };

    const valueStyle: React.CSSProperties = {
        width: '55%',
        textAlign: 'left',
        color: 'blue',
        fontWeight: 'bold'
    };

    const selectStyle: React.CSSProperties = {
        border: '1px solid #7F9DB9',
        padding: '3px',
        fontFamily: 'Arial',
        width: '100%',
        maxWidth: '220px',
        fontSize:"14pt",
        fontWeight:"bold",
        color:"blue"
    };

    return (
        <div style={containerStyle}>
       
            {loading && <Loading />}
            {confirmModal.show && (
                <CustomConfirmation
                    message={confirmModal.message}
                    onConfirm={confirmModal.onConfirm}
                    onClose={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                />
            )}

            {alertModal.show && (
                <CustomAlert
                    message={alertModal.message}
                    onClose={() => {
                        setAlertModal(prev => ({ ...prev, show: false }));
                        if (alertModal.onCloseCallback) {
                            alertModal.onCloseCallback();
                        }
                    }}
                />
            )}
            <div style={{ width: '100%', maxWidth: '100%', border: '1px solid #003366', boxSizing: 'border-box', paddingBottom:"30px"}}>
                     {/* <div
                style={{
                    backgroundImage: `url(${popimg}), url(${popbg})`,
                    backgroundRepeat: "no-repeat, no-repeat",
                    backgroundPosition: "left center, right center",
                    backgroundSize: "30% 100%, 70% 100%",
                    height: "16px",
                    color: "white",
                    padding: "7px 0px",
                    fontWeight: "bold",
                    fontSize: "12pt",
                    fontFamily: "arial",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    // marginBottom: "10px",
                }}
            >
                <span style={{ marginLeft: "10px" }}>
                    
                </span>
                <img
                    src={popclose}
                    alt="close"
                    height="30px"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/SysAdmin")}
                />
            </div> */}
                <div style={headerStyle}>
                    Creation of Default lesson plan
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Country:</div>
                    <div style={valueStyle}>{countryParam}</div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>School Code:</div>
                    <div style={valueStyle}>{schoolCode}</div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Family Code:</div>
                    <div style={valueStyle}>{familyCode}</div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Name:</div>
                    <div style={valueStyle}>{studentName}</div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Study Year:</div>
                    <div style={valueStyle}>{requestedStudyYear}</div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Lesson Plan Year:</div>
                    <div style={valueStyle}>
                        <select
                            style={selectStyle}
                            value={lessonPlanYear}
                            onChange={(e) => setLessonPlanYear(e.target.value)}
                        >
                            {YEAR_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Competency:</div>
                    <div style={valueStyle}>{getCompetencyLabel(competency)}</div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Lesson Plan Competency:</div>
                    <div style={valueStyle}>
                        <select
                            style={selectStyle}
                            value={String(targetCompetency)}
                            onChange={(e) => setTargetCompetency(parseInt(e.target.value, 10))}
                        >
                            {COMPETENCY_LEVELS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Subject:</div>
                    <div style={valueStyle}>{subject}</div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Curriculum:</div>
                    <div style={valueStyle}>{getCurriculum(countryParam)}</div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Current WLP:</div>
                    <div style={valueStyle}>{existingWLP}</div>
                </div>

                <div style={rowStyle}>
                    <div style={labelStyle}>Selective Student:</div>
                    <div style={valueStyle}>
                        <input
                            type="checkbox"
                            checked={isSelective}
                            onChange={(e) => setIsSelective(e.target.checked)}
                        />
                    </div>
                </div>

                <div style={{ marginTop: '25px', display: 'flex', gap: '15px', justifyContent:"center" }}>
                    <button
                        onClick={handleStartCreate}
                        style={{
                            padding: '3px 25px',
                            cursor: 'pointer',
                            border: '1px solid #767676',
                            backgroundColor: '#efefef',
                            borderRadius:"3px"
                            // fontWeight: 'bold'
                        }}
                    >
                        OK
                    </button>
                    <button
                        onClick={() => window.close()}
                        style={{
                            padding: '3px 25px',
                            cursor: 'pointer',
                            border: '1px solid #767676',
                            backgroundColor: '#efefef',
                            borderRadius:"3px"
                            // fontWeight: 'bold'
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

export default EL_CreateDefaultLessonPlan;